<template>
  <div class="connection-wrap" :class="activeTheme">
    <!-- Particles -->
    <div class="particles">
      <div class="bolt b1">⚡</div>
      <div class="bolt b2">⚡</div>
      <div class="star s1">✨</div>
      <div class="star s2">⭐</div>
      <div class="bubble bu1">o</div>
      <div class="bubble bu2">O</div>
    </div>

    <!-- Header -->
    <div class="header">
      <button class="back-btn" @click="goBack">← خروج</button>
      <div class="energy-display">
        <span class="energy-icon">🔋</span>
        <span class="energy-val">{{ currentEnergy }}%</span>
      </div>
    </div>

    <!-- 1. Selection Phase -->
    <div v-if="phase === 'select'" class="select-phase">
      <div class="pooyesh-status">
        <div class="avatar-wrapper">
           <img src="@/assets/logo.png" class="pooyesh-avatar tired" />
        </div>
        <div class="speech-bubble">
          سلام قهرمان! باتری من خالی شده 😟<br>
          لطفاً یکی از ماموریت‌های زیر رو انجام بده تا با انرژی دنیای واقعی شارژ بشم!
        </div>
      </div>

      <div class="missions-grid">
        <div class="mission-card kitchen" @click="startBriefing('kitchen')">
          <div class="card-icon">🍳</div>
          <div class="card-content">
            <h3>آشپزخونه جادویی</h3>
            <p>کمک به مامان یا بابا در آشپزی</p>
          </div>
        </div>
        <div class="mission-card workshop" @click="startBriefing('workshop')">
          <div class="card-icon">🛠️</div>
          <div class="card-content">
            <h3>کارگاه فنی</h3>
            <p>تعمیر یا ساخت کاردستی</p>
          </div>
        </div>
        <div class="mission-card league" @click="startBriefing('league')">
          <div class="card-icon">🦸</div>
          <div class="card-content">
            <h3>لیگ قهرمانان</h3>
            <p>بازی گروهی با دوستات</p>
          </div>
        </div>
      </div>
    </div>

    <!-- 2. Briefing Phase (AI giving task) -->
    <div v-if="phase === 'briefing'" class="briefing-phase">
      <div class="ai-task-card">
        <div class="ai-icon">🤖</div>
        <h2>ماموریت شما:</h2>
        <p class="task-text">{{ currentTaskText }}</p>
        <button class="action-btn start-mission" @click="enterCameraPhase">
          قبول ماموریت! 📸
        </button>
      </div>
    </div>

    <!-- 3. Camera/Upload Phase -->
    <div v-if="phase === 'mission'" class="mission-phase">
      <div class="step-card">
        <h2>مرحله {{ step }} از {{ totalSteps }}</h2>
        <p class="step-desc">{{ currentStepDesc }}</p>
        
        <div class="camera-box" @click="triggerCamera" :class="{ 'has-img': currentImage }">
          <img v-if="currentImage" :src="currentImage" class="preview-img" />
          <div v-else class="placeholder">
            <span class="cam-emoji">📸</span>
            <small>برای عکاسی ضربه بزنید</small>
          </div>
          <!-- Accept images -->
          <input type="file" ref="fileInput" accept="image/*" capture="environment" @change="onFileSelected" style="display:none" />
        </div>

        <div class="buttons-row">
          <button class="action-btn secondary" @click="triggerCamera" v-if="currentImage">
            تغییر عکس 🔄
          </button>
          <button class="action-btn primary" :disabled="!currentImage || uploading" @click="nextStep">
            {{ uploading ? 'در حال ارسال...' : (step === totalSteps ? 'پایان و دریافت انرژی ✨' : 'مرحله بعد ➡️') }}
          </button>
        </div>
      </div>
    </div>

    <!-- 4. Generating Phase (Polling) -->
    <div v-if="phase === 'generating'" class="generating-phase">
      <div class="scanner-anim">
        <div class="scan-line"></div>
        <img src="@/assets/logo.png" class="scan-avatar" />
      </div>
      <h3>در حال تحلیل انرژی... ⚡</h3>
      <p>پویش داره عکس‌های قشنگت رو بررسی می‌کنه!</p>
    </div>

    <!-- 5. Result Phase -->
    <div v-if="phase === 'result'" class="result-phase">
      <div class="confetti">🎉</div>
      <div class="result-header">
        <img src="@/assets/logo.png" class="pooyesh-avatar happy" />
        <div class="energy-burst">⚡ +{{ analysis?.powerScore || 100 }} انرژی</div>
      </div>
      
      <div class="reward-card">
        <h3 class="magic-title">{{ analysis?.magicTitle || 'ماموریت موفق!' }}</h3>
        
        <div class="caption-box">
          <span class="quote">“</span>
          {{ analysis?.funnyCaption || 'آفرین! خیلی عالی بود.' }}
          <span class="quote">”</span>
        </div>
        
        <div class="badges-grid" v-if="analysis?.badges?.length">
          <div v-for="b in analysis.badges" :key="b" class="badge">🏅 {{ b }}</div>
        </div>

        <div v-if="analysis?.superheroName" class="superhero-overlay">
          <div class="hero-label">نام تیم ابرقهرمانی شما:</div>
          <div class="hero-name">{{ analysis.superheroName }}</div>
        </div>
      </div>

      <div class="result-actions">
        <button class="action-btn replay" @click="resetToMenu">انجام ماموریت جدید 🔄</button>
        <button class="action-btn home" @click="goBack">بازگشت به اتاق 🏠</button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onUnmounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useMutation, useApolloClient } from '@vue/apollo-composable'
import { START_CONNECTION_MUTATION, UPLOAD_CONNECTION_STEP_MUTATION, COMPLETE_CONNECTION_MUTATION } from '../api/mutations'
import { CHECK_CONNECTION_STATUS_QUERY } from '../api/queries'

const router = useRouter()
const route = useRoute()
const slug = route.params.slug
const { client } = useApolloClient()

// State
const phase = ref('select') 
const missionType = ref('')
const step = ref(1)
const sessionId = ref(null)
const currentImage = ref(null)
const uploading = ref(false)
const analysis = ref(null)
const fileInput = ref(null)
const currentEnergy = ref(35) 
let pollInterval = null

// Mutations
const { mutate: startMutate } = useMutation(START_CONNECTION_MUTATION)
const { mutate: uploadMutate } = useMutation(UPLOAD_CONNECTION_STEP_MUTATION)
const { mutate: completeMutate } = useMutation(COMPLETE_CONNECTION_MUTATION)

// --- Logic ---

const activeTheme = computed(() => {
  if (missionType.value === 'kitchen') return 'theme-orange'
  if (missionType.value === 'workshop') return 'theme-blue'
  if (missionType.value === 'league') return 'theme-purple'
  return 'theme-default'
})

const totalSteps = computed(() => {
  if (missionType.value === 'league') return 1
  return 2 
})

// Dynamic Tasks
const tasksDB = {
  kitchen: [
    "امروز باید یه سالاد رنگی‌رنگی درست کنیم! 🥗",
    "ماموریت: پیدا کردن ۳ تا میوه قرمز و شستن اون‌ها! 🍎🍓🍒",
    "کمک به مامان برای چیدن میز غذا به شکل رستوران! 🍽️"
  ],
  workshop: [
    "ماموریت: پیدا کردن یه اسباب‌بازی خراب و تعمیر اون با بابا! 🔧",
    "ساختن یه برج بلند با لگو یا وسایل دورریختنی! 🏰",
    "مرتب کردن جعبه ابزار و یاد گرفتن اسم ابزارها! 🔨"
  ],
  league: [
    "یه عکس دست‌جمعی با فیگور ابرقهرمانی بگیرید! 🦸‍♂️",
    "مسابقه شکلک درآوردن با دوستات! 🤪",
    "ساختن یه قلعه با بالش‌ها و پتوها! ⛺"
  ]
}

const currentTaskText = computed(() => {
  if (!missionType.value) return ''
  const list = tasksDB[missionType.value]
  return list[Math.floor(Math.random() * list.length)]
})

const currentStepDesc = computed(() => {
  if (missionType.value === 'kitchen') {
    return step.value === 1 ? 'از مواد اولیه‌ای که پیدا کردی عکس بگیر' : 'حالا از نتیجه خوشمزه کار عکس بگیر!'
  }
  if (missionType.value === 'workshop') {
    return step.value === 1 ? 'از چیزی که قراره بسازی یا تعمیر کنی عکس بگیر' : 'از شاهکاری که ساختی عکس بگیر!'
  }
  return 'همه با هم بگید سییییب! 📸'
})

// --- Methods ---

async function startBriefing(type) {
  missionType.value = type
  try {
    const { data } = await startMutate({ data: { roomSlug: slug, missionType: type } })
    sessionId.value = data.startConnectionMission.sessionId
    phase.value = 'briefing'
  } catch (e) {
    alert('خطا در اتصال به مرکز فرماندهی!')
  }
}

function enterCameraPhase() {
  step.value = 1
  currentImage.value = null
  phase.value = 'mission'
}

function triggerCamera() {
  fileInput.value.click()
}

// === FIXED: Image Conversion Logic ===
function onFileSelected(e) {
  const file = e.target.files[0]
  if (file) {
    const reader = new FileReader()
    reader.onload = (event) => {
      // Create an image object to load the file
      const img = new Image()
      img.onload = () => {
        // Create canvas to draw the image (effectively converting format)
        const canvas = document.createElement('canvas')
        
        // Resize logic (Max 1024px to save bandwidth/processing)
        const maxDim = 1024
        let width = img.width
        let height = img.height
        
        if (width > maxDim || height > maxDim) {
          if (width > height) {
            height *= maxDim / width
            width = maxDim
          } else {
            width *= maxDim / height
            height = maxDim
          }
        }
        
        canvas.width = width
        canvas.height = height
        
        const ctx = canvas.getContext('2d')
        ctx.drawImage(img, 0, 0, width, height)
        
        // Convert to JPEG (compatible with Gemini)
        currentImage.value = canvas.toDataURL('image/jpeg', 0.8)
      }
      img.src = event.target.result
    }
    reader.readAsDataURL(file)
  }
}

async function nextStep() {
  if (!currentImage.value) return
  uploading.value = true
  
  try {
    await uploadMutate({ 
      data: { 
        sessionId: sessionId.value, 
        imageBase64: currentImage.value, 
        stepIndex: step.value 
      } 
    })

    if (step.value < totalSteps.value) {
      step.value++
      currentImage.value = null
    } else {
      await completeMutate({ sessionId: sessionId.value })
      phase.value = 'generating'
      pollInterval = setInterval(checkAnalysisStatus, 3000)
    }
  } catch (e) {
    alert('خطا در ارسال عکس')
  } finally {
    uploading.value = false
  }
}

async function checkAnalysisStatus() {
  try {
    const { data } = await client.query({
      query: CHECK_CONNECTION_STATUS_QUERY,
      variables: { sessionId: sessionId.value },
      fetchPolicy: 'network-only'
    })

    const result = data?.checkConnectionStatus?.analysis
    if (result && result.badges && result.magicTitle) {
      clearInterval(pollInterval)
      analysis.value = result
      const gain = result.powerScore || 50
      currentEnergy.value = Math.min(100, currentEnergy.value + Math.floor(gain / 5))
      phase.value = 'result'
    }
  } catch (e) {
    console.error("Polling error", e)
  }
}

function resetToMenu() {
  phase.value = 'select'
  missionType.value = ''
  sessionId.value = null
  analysis.value = null
  if (pollInterval) clearInterval(pollInterval)
}

function goBack() {
  router.push(`/room/${slug}`)
}

onUnmounted(() => {
  if (pollInterval) clearInterval(pollInterval)
})
</script>

<style scoped>
.connection-wrap {
  min-height: 100vh;
  padding: 20px;
  background: #f8fafc;
  font-family: 'Vazirmatn', sans-serif;
  text-align: center;
  overflow-x: hidden;
  position: relative;
}

/* Particles */
.particles { position: fixed; inset: 0; pointer-events: none; z-index: 0; }
.bolt, .star, .bubble { position: absolute; opacity: 0.2; animation: float 10s infinite linear; font-size: 24px; }
.b1 { top: 10%; left: 10%; animation-delay: 0s; }
.b2 { top: 60%; right: 15%; animation-delay: 2s; }
.s1 { top: 30%; right: 20%; animation-delay: 1s; }
.s2 { top: 80%; left: 25%; animation-delay: 3s; }
.bu1 { bottom: 10%; left: 50%; animation: rise 8s infinite ease-in; font-size: 30px; }
.bu2 { bottom: 5%; left: 40%; animation: rise 6s infinite ease-in 1s; font-size: 20px; }

@keyframes float { 0% { transform: translateY(0) rotate(0deg); } 50% { transform: translateY(-20px) rotate(10deg); } 100% { transform: translateY(0) rotate(0deg); } }
@keyframes rise { 0% { bottom: -20px; opacity: 0; } 50% { opacity: 0.3; } 100% { bottom: 100vh; opacity: 0; } }

/* Themes */
.theme-default { background: linear-gradient(135deg, #e2e8f0 0%, #cbd5e1 100%); }
.theme-orange { background: linear-gradient(135deg, #ffedd5 0%, #fed7aa 100%); }
.theme-blue { background: linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%); }
.theme-purple { background: linear-gradient(135deg, #f3e8ff 0%, #d8b4fe 100%); }

/* Header */
.header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 24px; position: relative; z-index: 10; }
.back-btn { background: rgba(255,255,255,0.6); border: 2px solid #fff; padding: 8px 16px; border-radius: 99px; font-weight: 700; cursor: pointer; color: #475569; }
.energy-display { background: #fff; padding: 6px 12px; border-radius: 20px; border: 2px solid #22c55e; display: flex; align-items: center; gap: 6px; box-shadow: 0 4px 10px rgba(34,197,94,0.2); }
.energy-val { font-weight: 800; color: #15803d; }

/* Select Phase */
.select-phase { max-width: 600px; margin: 0 auto; position: relative; z-index: 10; }
.pooyesh-status { display: flex; align-items: center; gap: 16px; margin-bottom: 30px; background: rgba(255,255,255,0.7); padding: 16px; border-radius: 24px; text-align: right; }
.pooyesh-avatar { width: 80px; height: 80px; }
.pooyesh-avatar.tired { filter: grayscale(0.5) contrast(0.8); }
.speech-bubble { background: #fff; padding: 12px; border-radius: 16px; border-top-right-radius: 0; font-size: 14px; color: #334155; line-height: 1.5; box-shadow: 0 4px 12px rgba(0,0,0,0.05); position: relative; }
.speech-bubble::after { content: ''; position: absolute; top: 0; right: -10px; border: 10px solid transparent; border-top-color: #fff; border-right: 0; }

.missions-grid { display: grid; gap: 16px; grid-template-columns: 1fr; }
.mission-card { background: #fff; padding: 16px; border-radius: 20px; cursor: pointer; transition: transform 0.2s; display: flex; align-items: center; gap: 16px; text-align: right; border: 3px solid transparent; box-shadow: 0 4px 12px rgba(0,0,0,0.05); }
.mission-card:hover { transform: scale(1.02); }
.mission-card.kitchen { border-color: #f97316; }
.mission-card.kitchen .card-icon { background: #ffedd5; }
.mission-card.workshop { border-color: #3b82f6; }
.mission-card.workshop .card-icon { background: #dbeafe; }
.mission-card.league { border-color: #a855f7; }
.mission-card.league .card-icon { background: #f3e8ff; }
.card-icon { width: 60px; height: 60px; border-radius: 16px; display: flex; align-items: center; justify-content: center; font-size: 32px; }
.card-content h3 { margin: 0 0 4px; font-size: 18px; color: #1e293b; }
.card-content p { margin: 0; font-size: 13px; color: #64748b; }

/* Briefing Phase */
.briefing-phase { max-width: 500px; margin: 0 auto; position: relative; z-index: 10; display: flex; align-items: center; height: 70vh; }
.ai-task-card { background: #fff; border-radius: 32px; padding: 32px; width: 100%; box-shadow: 0 20px 50px rgba(0,0,0,0.1); border: 4px solid #fff; }
.ai-icon { font-size: 64px; margin-bottom: 16px; animation: bounce 2s infinite; }
.ai-task-card h2 { color: #3b82f6; margin-bottom: 16px; }
.task-text { font-size: 20px; font-weight: 700; color: #1e293b; margin-bottom: 32px; line-height: 1.6; }
.action-btn.start-mission { background: #22c55e; width: 100%; padding: 16px; border-radius: 16px; color: #fff; font-size: 18px; font-weight: 800; border: none; cursor: pointer; box-shadow: 0 8px 20px rgba(34, 197, 94, 0.4); }

/* Mission Phase */
.mission-phase { max-width: 500px; margin: 0 auto; position: relative; z-index: 10; }
.step-card { background: #fff; padding: 24px; border-radius: 24px; box-shadow: 0 10px 30px rgba(0,0,0,0.1); }
.step-desc { font-size: 18px; color: #334155; margin-bottom: 20px; }
.camera-box { width: 100%; height: 300px; background: #f1f5f9; border-radius: 20px; display: flex; align-items: center; justify-content: center; overflow: hidden; border: 4px dashed #cbd5e1; cursor: pointer; transition: all 0.3s; position: relative; }
.camera-box.has-img { border-style: solid; border-color: #2563eb; }
.preview-img { width: 100%; height: 100%; object-fit: cover; }
.placeholder { color: #94a3b8; display: flex; flex-direction: column; gap: 10px; }
.cam-emoji { font-size: 48px; opacity: 0.5; }
.buttons-row { display: flex; gap: 12px; margin-top: 20px; }
.action-btn { flex: 1; padding: 14px; border-radius: 14px; font-weight: 700; border: none; cursor: pointer; font-size: 16px; transition: transform 0.2s; }
.action-btn:active { transform: scale(0.98); }
.action-btn.primary { background: #2563eb; color: #fff; box-shadow: 0 4px 12px rgba(37, 99, 235, 0.3); }
.action-btn.primary:disabled { background: #94a3b8; box-shadow: none; cursor: not-allowed; }
.action-btn.secondary { background: #eff6ff; color: #2563eb; }

/* Generating Phase */
.generating-phase { display: flex; flex-direction: column; align-items: center; justify-content: center; height: 60vh; z-index: 10; position: relative; }
.scanner-anim { width: 120px; height: 120px; position: relative; margin-bottom: 30px; }
.scan-avatar { width: 100%; height: 100%; opacity: 0.8; }
.scan-line { position: absolute; top: 0; left: 0; width: 100%; height: 4px; background: #3b82f6; box-shadow: 0 0 10px #3b82f6; animation: scan 2s infinite linear; }
@keyframes scan { 0% { top: 0; opacity: 0; } 20% { opacity: 1; } 80% { opacity: 1; } 100% { top: 100%; opacity: 0; } }

/* Result Phase */
.result-phase { padding-top: 20px; max-width: 600px; margin: 0 auto; position: relative; z-index: 10; }
.result-header { position: relative; display: inline-block; margin-bottom: 20px; }
.pooyesh-avatar.happy { width: 140px; height: 140px; animation: bounce 2s infinite; }
.energy-burst { position: absolute; top: -10px; right: -20px; background: #eab308; color: #fff; padding: 6px 12px; border-radius: 99px; font-weight: 900; font-size: 18px; transform: rotate(10deg); box-shadow: 0 4px 10px rgba(234, 179, 8, 0.4); animation: pulse 1s infinite; }

.reward-card { background: #fff; padding: 32px; border-radius: 32px; box-shadow: 0 20px 60px rgba(0,0,0,0.15); border: 4px solid #fff; position: relative; overflow: hidden; margin-bottom: 24px; }
.magic-title { font-size: 28px; color: #0f172a; margin: 0 0 16px; background: linear-gradient(135deg, #3b82f6, #8b5cf6); -webkit-background-clip: text; -webkit-text-fill-color: transparent; }
.caption-box { background: #f8fafc; padding: 20px; border-radius: 16px; font-size: 18px; color: #475569; margin-bottom: 24px; font-style: italic; position: relative; }
.quote { color: #cbd5e1; font-size: 40px; position: absolute; line-height: 0; }
.quote:first-child { top: 15px; right: 10px; }
.quote:last-child { bottom: 0px; left: 10px; }

.badges-grid { display: flex; flex-wrap: wrap; justify-content: center; gap: 10px; margin-bottom: 20px; }
.badge { background: #fef9c3; color: #b45309; padding: 8px 16px; border-radius: 99px; font-weight: bold; border: 2px solid #fde047; box-shadow: 0 2px 4px rgba(0,0,0,0.05); }

.superhero-overlay { background: #f0f9ff; padding: 16px; border-radius: 16px; border: 2px dashed #bae6fd; }
.hero-label { font-size: 12px; color: #64748b; margin-bottom: 4px; }
.hero-name { font-size: 20px; font-weight: 900; color: #0369a1; }

.result-actions { display: flex; flex-direction: column; gap: 12px; }
.action-btn.replay { background: #10b981; color: #fff; }
.action-btn.home { background: #f1f5f9; color: #475569; }

@keyframes bounce { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-10px); } }
@keyframes pulse { 0% { transform: rotate(10deg) scale(1); } 50% { transform: rotate(10deg) scale(1.1); } 100% { transform: rotate(10deg) scale(1); } }
</style>